import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, InputAdornment, TextField, Typography, useTheme } from '@mui/material';
import { AddFilled, ChevronDownRegular, InfoFilled, PersonInfoFilled, PersonWarningFilled, RadioButtonFilled, WarningRegular } from '@fluentui/react-icons';
import { between } from '@ui/style/style';
import useScorecardConfigurationStyles from './useScorecardConfigurationStyles';
import { AddNewCriteriaDialog, ConfigureMetricsDialog } from '@ui/components/Dialogs';
import { useDispatch, useSelector } from 'react-redux';
import { getScoreCard, updateCriteria } from '@application/redux/scoreCard/score-card-slice';
import TimeRangePicker from '@ui/components/common/TimeRangePicker/index';
const DescriptionField = ({ name, label, type = 'string', value, adorment, onChangeValue, onBlur, disabled = false }) => {
    return (_jsxs(Box, { sx: { ...between }, children: [_jsx(Typography, { variant: 'body2', children: name }), _jsx(TextField, { variant: 'outlined', inputProps: {
                    min: 0
                }, size: 'small', value: value, type: type, disabled: disabled, onChange: onChangeValue, onBlur: onBlur, sx: {
                    maxWidth: 250,
                    width: '100%'
                }, slotProps: {
                    input: {
                        endAdornment: _jsx(InputAdornment, { position: 'end', children: adorment || label })
                    }
                } })] }));
};
const ScorecardConfiguration = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { contentContainerStyles, headerStyles, rowStyles } = useScorecardConfigurationStyles();
    const dispatch = useDispatch();
    const { getScoreCardSetting, isCreated, isMetricConfigured, scoreMetrics } = useSelector((state) => state.scoreCardSlice);
    useEffect(() => {
        dispatch(getScoreCard('86AAC17D-397E-404F-9242-C4F1F2052B34'));
    }, [dispatch, isCreated, isMetricConfigured]);
    function getCategoryByScore(score) {
        if (!Array.isArray(getScoreCardSetting.scoreCardMerits) ||
            getScoreCardSetting.scoreCardMerits.length === 0) {
            return 'un-available';
        }
        for (const metric of getScoreCardSetting.scoreCardMerits) {
            if (score >= metric.lowerLimit && score <= metric.upperLimit) {
                return metric.categoryName;
            }
            if (score > 100) {
                return getScoreCardSetting.scoreCardMerits.at(-1)?.categoryName;
            }
        }
        return '';
    }
    const [expanded, setExpanded] = React.useState(false);
    const [cardsObj, setCardsObj] = useState();
    const [showAddNewCriteriaDialog, setShowAddNewCriteriaDialog] = useState(false);
    useEffect(() => {
        setCardsObj(getScoreCardSetting);
    }, [getScoreCardSetting]);
    const handleOpenAddNewCriteriaDialog = () => {
        setShowAddNewCriteriaDialog(true);
    };
    const handleCloseAddNewCriteriaDialog = () => {
        setShowAddNewCriteriaDialog(false);
    };
    const [showConfigureMetricDialog, setShowConfigureMetricDialog] = useState(false);
    const handleOpenConfigureMetricDialog = () => {
        setShowConfigureMetricDialog(true);
    };
    const handleCloseConfigureMetricDialog = () => {
        setShowConfigureMetricDialog(false);
    };
    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };
    const handleChangePoints = (event, cardId, field) => {
        const updatedValue = event.target.value;
        setCardsObj((prev) => {
            if (!prev)
                return prev;
            return {
                ...prev,
                scoreCardData: prev.scoreCardData.map((card) => {
                    if (card.cardId !== cardId)
                        return card;
                    return {
                        ...card,
                        fields: card.fields.map((f) => {
                            if (f.fieldName !== field.fieldName)
                                return f;
                            if (f.fieldName === 'description') {
                                return {
                                    ...f,
                                    fieldPoints: +updatedValue
                                };
                            }
                            return {
                                ...f,
                                fieldValue: updatedValue
                            };
                        })
                    };
                })
            };
        });
    };
    const handleBlur = async (cardId, field) => {
        const payload = {
            id: field.id,
            assetId: '86AAC17D-397E-404F-9242-C4F1F2052B34',
            cardId,
            score: field.fieldPoints,
            filedValue: field.fieldValue
        };
        dispatch(updateCriteria(payload))
            .unwrap()
            .then()
            .catch(() => { });
    };
    const ScoringLegend = ({ Icon, iconColor, iconType, name }) => {
        const renderIcon = () => {
            switch (iconType) {
                case 'radio':
                    return Icon ? _jsx(PersonInfoFilled, { fontSize: 24, color: '#53E0BC' }) : null;
                case 'danger':
                    return _jsx(WarningRegular, { fontSize: 24, color: '#E44236' });
                case 'neutral':
                    return _jsx(InfoFilled, { fontSize: 24, color: '#26ae60' });
                case 'warning':
                    return _jsx(PersonWarningFilled, { fontSize: 24, color: '#F3B63A' });
                default:
                    return null;
            }
        };
        return (_jsxs(Box, { sx: { ...between }, children: [_jsx(Typography, { variant: 'body2', children: "Scoring Legend" }), _jsxs(Box, { sx: {
                        display: 'flex',
                        gap: 1,
                        borderRadius: 1,
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        py: 1,
                        px: '14px',
                        maxWidth: 250,
                        width: '100%'
                    }, children: [renderIcon(), _jsx(Typography, { variant: 'body2', sx: { textOverflow: 'ellipsis', overflow: 'hidden' }, children: name })] })] }));
    };
    return (_jsxs(Card, { className: 'section section__scorecard-configuration', sx: { width: '100%' }, children: [_jsxs(Box, { className: 'header-section', sx: headerStyles, children: [_jsxs(Box, { sx: rowStyles, children: [_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 0.5 }, children: _jsx(Typography, { variant: 'subtitle1', children: t('ScoreConfiguration') }) }), _jsx(Box, { sx: { display: 'flex', gap: 1 } })] }), _jsx(Box, { sx: rowStyles, children: _jsx(Box, { sx: { display: 'flex', flexDirection: 'row', gap: 1.5 }, children: _jsx(Button, { color: 'inherit', variant: 'contained', startIcon: _jsx(AddFilled, { fontSize: 20 }), onClick: handleOpenAddNewCriteriaDialog, children: t('AddNewCriteria') }) }) })] }), _jsx(Box, { className: 'content-container', sx: contentContainerStyles, children: cardsObj &&
                    cardsObj.scoreCardData?.map((item, index) => (_jsxs(Accordion, { defaultExpanded: true, onChange: handleChange('panel1'), children: [_jsx(AccordionSummary, { expandIcon: _jsx(ChevronDownRegular, { fontSize: 20, color: theme.palette.text.primary }), "aria-controls": 'panel1bh-content', id: 'panel1bh-header', children: _jsx(Box, { sx: { ...between, gap: 1.5 }, children: _jsx(Box, { sx: { display: 'flex', flexDirection: 'row', gap: 1.5, alignItems: 'center' }, children: _jsx(Typography, { variant: 'subtitle1', children: item.fields.find((f) => f.fieldName === 'title')?.fieldValue }) }) }) }), _jsxs(AccordionDetails, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: [item.fields.find((f) => f.fieldName === 'distance' && f.isActive) && (_jsx(DescriptionField, { name: 'Radius of the start location', label: 'distance', type: 'number', adorment: 'KM', disabled: true, value: +(item.fields.find((f) => f.fieldName === 'distance')?.fieldValue ?? 0), onChangeValue: (ev) => handleChangePoints(ev, item.cardId, item.fields.find((f) => f.fieldName === 'distance')), onBlur: () => handleBlur(item.cardId, item.fields.find((f) => f.fieldName === 'distance')) })), item.fields.find((f) => f.fieldName === 'timeRange' && f.isActive) && (_jsxs(Box, { sx: { ...between }, children: [_jsx(Typography, { variant: 'body2', children: "Peak Hour Duration" }), _jsx(TimeRangePicker, { disabled: true, cardId: item.cardId, fieldValues: item.fields.find((f) => f.fieldName === 'timeRange') })] })), item.fields.find((f) => f.fieldName === 'timeSensitivityDescription' && f.isActive) && (_jsx(Box, { sx: { ...between }, children: _jsx(DescriptionField, { name: t('TimeSensitivityDescription'), label: 'distance', type: 'number', adorment: 'Mins', disabled: true, value: +(item.fields.find((f) => f.fieldName === 'timeSensitivityDescription')
                                                ?.fieldValue ?? 0), onChangeValue: (ev) => handleChangePoints(ev, item.cardId, item.fields.find((f) => f.fieldName === 'timeSensitivityDescription')), onBlur: () => handleBlur(item.cardId, item.fields.find((f) => f.fieldName === 'timeSensitivityDescription')) }) })), item.fields.find((f) => f.fieldName === 'description' && f.isActive) && (_jsxs(Box, { sx: { ...between }, children: [_jsx(Typography, { variant: 'body2', children: item.fields.find((f) => f.fieldName === 'description')?.fieldValue }), _jsx(TextField, { type: 'number', variant: 'outlined', size: 'small', label: 'Points', value: item.fields.find((f) => f.fieldName === 'description')?.fieldPoints ?? '', onChange: (ev) => handleChangePoints(ev, item.cardId, item.fields.find((f) => f.fieldName === 'description')), onBlur: (ev) => handleBlur(item.cardId, item.fields.find((f) => f.fieldName === 'description')), inputProps: {
                                                    min: 0,
                                                    max: 100,
                                                    onFocus: (e) => {
                                                        e.target.select();
                                                    },
                                                    onInput: (e) => {
                                                        const input = e.currentTarget;
                                                        let v = input.value;
                                                        if (/^0+\d/.test(v)) {
                                                            v = v.replace(/^0+/, '');
                                                        }
                                                        const n = parseInt(v, 10);
                                                        if (!isNaN(n)) {
                                                            input.value = String(Math.min(100, Math.max(0, n)));
                                                        }
                                                    }
                                                }, sx: { maxWidth: 250, width: '100%' } })] })), (() => {
                                        const points = item.fields.find((f) => f.fieldName === 'description')?.fieldPoints ?? 0;
                                        const category = getCategoryByScore(points) ?? '';
                                        // iconType must match what's expected in ScoringLegend
                                        const getIconTypeByCategory = (category) => {
                                            switch (category) {
                                                case 'No Alarm':
                                                    return 'neutral';
                                                case 'Average':
                                                    return 'radio';
                                                case 'High':
                                                    return 'warning';
                                                default:
                                                    return 'danger';
                                            }
                                        };
                                        // Only pass Icon if iconType === 'radio'
                                        const iconType = getIconTypeByCategory(category);
                                        const icon = iconType === 'radio' ? RadioButtonFilled : undefined;
                                        return (_jsx(ScoringLegend, { Icon: icon, iconColor: theme.palette.grey[500], name: category, iconType: iconType }));
                                    })()] })] }, item.cardId))) }), !!showAddNewCriteriaDialog && (_jsx(AddNewCriteriaDialog, { isOpen: showAddNewCriteriaDialog, onClose: handleCloseAddNewCriteriaDialog })), !!showConfigureMetricDialog && (_jsx(ConfigureMetricsDialog, { isOpen: showConfigureMetricDialog, onClose: handleCloseConfigureMetricDialog }))] }));
};
export default ScorecardConfiguration;
